import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { QUERY_KEY_INITIALS } from '@/shared/constants/query-key-initials';
import { useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';
import {
	type WorkspaceLocationsResponse,
	fetchWorkspaceLocations,
} from '@/shared/services/page/workspace-locations';
import { ensureCitiesReady, searchCities } from '@/shared/utils/cities';

type UseLocationSearchParams = {
	selectedLocations: string[];
	onLocationsChange: (locations: string[]) => void;
	isStaticMode?: boolean;
};

/**
 * Generic location search hook.
 * Owns the workspace-locations query, the search input state, and all
 * derived city-list memos. Works with any selectedLocations / onLocationsChange
 * source — use it in both the inline popover and the Live Stats modal.
 */
export function useLocationSearch({
	selectedLocations,
	onLocationsChange,
	isStaticMode = false,
}: UseLocationSearchParams) {
	const pageId = useAppStore((state) => state.selectedWorkspace?.id ?? '');
	const queryClient = useQueryClient();

	const [search, setSearch] = useState('');
	// Tracks whether the cities-list chunk has resolved. Triggers a re-render
	// once ready so citySearchResults can recompute with the full index.
	const [citiesReady, setCitiesReady] = useState(false);

	useEffect(() => {
		let cancelled = false;
		ensureCitiesReady().then(() => {
			if (!cancelled) setCitiesReady(true);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	const { data: workspaceLocationsData } = useQuery({
		queryKey: [QUERY_KEY_INITIALS.WORKSPACE_LOCATIONS, pageId],
		queryFn: () => fetchWorkspaceLocations(pageId),
		enabled: !!pageId && !isStaticMode,
		staleTime: 10 * 60 * 1000,
	});

	const recentLocations = useMemo(
		() => workspaceLocationsData?.locations ?? [],
		[workspaceLocationsData]
	);

	const recentSet = useMemo(
		() => new Set(recentLocations.map((c) => c.toLowerCase())),
		[recentLocations]
	);

	const trimmedSearch = search.trim();

	const selectedSet = useMemo(
		() => new Set(selectedLocations.map((c) => c.toLowerCase())),
		[selectedLocations]
	);

	const citySearchResults = useMemo(() => {
		const results = searchCities(search).filter((c) => !recentSet.has(c.toLowerCase()));
		return [...results].sort((a, b) => {
			const aSelected = selectedSet.has(a.toLowerCase());
			const bSelected = selectedSet.has(b.toLowerCase());
			if (aSelected === bSelected) return 0;
			return aSelected ? -1 : 1;
		});
		// citiesReady triggers re-compute once the cities chunk resolves.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, recentSet, selectedSet, citiesReady]);

	const filteredRecent = useMemo(() => {
		const results = trimmedSearch
			? recentLocations.filter((c) => c.toLowerCase().includes(trimmedSearch.toLowerCase()))
			: recentLocations;
		return [...results].sort((a, b) => {
			const aSelected = selectedSet.has(a.toLowerCase());
			const bSelected = selectedSet.has(b.toLowerCase());
			if (aSelected === bSelected) return 0;
			return aSelected ? -1 : 1;
		});
	}, [trimmedSearch, recentLocations, selectedSet]);

	const showCustomAdd = useMemo(
		() =>
			!!trimmedSearch &&
			!citySearchResults.some((c) => c.toLowerCase() === trimmedSearch.toLowerCase()) &&
			!recentLocations.some((c) => c.toLowerCase() === trimmedSearch.toLowerCase()) &&
			!selectedLocations.includes(trimmedSearch),
		[trimmedSearch, citySearchResults, recentLocations, selectedLocations]
	);

	const handleSelectCity = useCallback(
		(city: string) => {
			const isRemoving = selectedLocations.includes(city);
			const newLocations = isRemoving
				? selectedLocations.filter((l) => l !== city)
				: [...selectedLocations, city];
			onLocationsChange(newLocations);

			// Optimistically add newly selected city to the workspace locations cache
			// so it immediately appears under "Recent" without waiting for a refetch.
			if (!isRemoving && !recentSet.has(city.toLowerCase())) {
				const cacheKey = [QUERY_KEY_INITIALS.WORKSPACE_LOCATIONS, pageId];
				queryClient.setQueryData<WorkspaceLocationsResponse>(cacheKey, (prev) => ({
					locations: [city, ...(prev?.locations ?? [])],
				}));
			}

			// Only clear the search input when the user typed a custom city and
			// confirmed it — not when toggling an existing item from the list.
			if (city.toLowerCase() === search.trim().toLowerCase()) {
				setSearch('');
			}
		},
		[selectedLocations, onLocationsChange, recentSet, pageId, queryClient, search]
	);

	return {
		search,
		setSearch,
		trimmedSearch,
		recentLocations,
		citySearchResults,
		filteredRecent,
		showCustomAdd,
		handleSelectCity,
	};
}
